import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@heroui/react';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import { stripMarkdown } from '@/lib/markdown';
import { formatTime } from '@/lib/format';
import { usePinnedMessages } from '@/lib/usePinnedMessages';
import { ChevronDownIcon, CloseIcon, PinIcon } from '@/components/icons';
import { Tooltip } from '@/components/Tooltip';
import { emitAppEvent } from '@/lib/appEvents';
import type { Message } from '@/types/api';

/**
 * Pinned-message dock under the channel header (Telegram pattern).
 *
 * When the channel has pinned messages, this thin strip shows the
 * most recent one — author + snippet — always visible. Clicking the
 * strip toggles a small expand to show the rest. So pinned content
 * is glanceable without having to hunt for the pin-icon popover.
 *
 * Hidden when there are zero pinned messages (no dead space).
 */
export function PinnedBar(props: { channelId: string }) {
  const { t } = useTranslation();
  const { messages, loading, error, refetch } = usePinnedMessages(props.channelId);
  const [expanded, setExpanded] = useState(false);

  // Reset expanded state on channel switch (otherwise we'd carry over
  // expansion to a channel that may have nothing to expand).
  useEffect(() => {
    setExpanded(false);
  }, [props.channelId]);

  // Fetch error → show a recoverable strip instead of silently hiding.
  if (!loading && error) {
    return (
      <div
        className="flex items-center gap-3 px-5 py-1.5"
        style={{
          backgroundColor: 'rgb(var(--chrome-bg) / var(--chrome-bg-alpha))',
          backdropFilter: 'blur(6px) saturate(140%)',
          WebkitBackdropFilter: 'blur(6px) saturate(140%)',
          boxShadow: 'var(--chrome-edge-bottom)',
        }}
      >
        <span className="text-xs text-danger">{t('ui.pinned.loadFailed')}</span>
        <button
          type="button"
          onClick={refetch}
          className="text-xs text-accent underline-offset-2 hover:underline"
        >
          {t('ui.pinned.retry')}
        </button>
      </div>
    );
  }

  if (!messages || messages.length === 0) return null;
  const latest = messages[0];
  if (!latest) return null;
  const preview = stripMarkdown(latest.content) || t('ui.pinned.emptyContent');
  const author = latest.author.displayName || latest.author.username;
  const hasMore = messages.length > 1;

  function jumpTo(messageId: string) {
    emitAppEvent('chat:scroll-to-message', { messageId });
  }

  // Click on the strip: if there's only one pinned message, jump to it
  // directly (there's nothing to expand). With 2+ pins, toggle the
  // expand panel — power users can scan the list, then click a row to
  // jump. Telegram does the same dual behaviour.
  function handleStripClick() {
    if (!hasMore) {
      jumpTo(latest!.id);
      return;
    }
    setExpanded((v) => !v);
  }

  return (
    <div
      className="chat-pin-container shrink-0"
      style={{
        backgroundColor: 'rgb(var(--chrome-bg) / var(--chrome-bg-alpha))',
        backdropFilter: 'blur(6px) saturate(140%)',
        WebkitBackdropFilter: 'blur(6px) saturate(140%)',
        boxShadow: 'var(--chrome-edge-bottom)',
      }}
    >
      <button
        type="button"
        onClick={handleStripClick}
        title={hasMore ? t('ui.pinned.expand') : t('ui.pinned.jumpToPinned')}
        className="chat-pinned-bar flex w-full items-center gap-3 px-5 py-2 text-left transition-colors hover:bg-white/6"
      >
        <span className="chat-pin-icon grid h-5 w-5 shrink-0 place-items-center text-ink-muted">
          <PinIcon className="h-3.5 w-3.5" />
        </span>
        <div className="flex min-w-0 flex-1 items-baseline gap-2">
          <span className="chat-pin-label shrink-0 text-xs font-medium text-ink-muted">
            {t('ui.pinned.pinnedLabel')}
          </span>
          <span className="max-w-24 truncate text-xs font-medium text-ink">{author}</span>
          <span className="min-w-0 flex-1 truncate text-xs text-ink-dim">{preview}</span>
        </div>
        <span className="shrink-0 text-[10px] tabular-nums text-ink-muted">
          {hasMore ? `+${messages.length - 1}` : ''}
        </span>
        {hasMore && (
          <ChevronDownIcon
            className={
              'h-4 w-4 shrink-0 text-ink-muted transition-transform ' +
              (expanded ? 'rotate-180' : '')
            }
          />
        )}
      </button>
      {expanded && hasMore && (
        <div
          className="scrollbar-thin max-h-[40vh] overflow-y-auto px-2 pb-2"
          style={{
            backgroundColor: 'rgb(var(--surface-card) / 0.4)',
            backdropFilter: 'blur(6px) saturate(140%)',
            WebkitBackdropFilter: 'blur(6px) saturate(140%)',
          }}
        >
          {messages.slice(1).map((m) => (
            <PinnedExpandedRow
              key={m.id}
              message={m}
              onJump={() => jumpTo(m.id)}
              onUnpinned={refetch}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One row in the expanded pinned-bar dropdown. Pulled into its own
 * component so each row can own its hover-state for the unpin button
 * without dragging mouseEnter/mouseLeave logic into the parent.
 */
function PinnedExpandedRow(props: {
  message: Message;
  onJump: () => void;
  onUnpinned: () => void;
}) {
  const { t } = useTranslation();
  const [unpinning, setUnpinning] = useState(false);
  async function onUnpin(e: React.MouseEvent) {
    e.stopPropagation();
    if (unpinning) return;
    setUnpinning(true);
    try {
      await api.unpinMessage(props.message.id);
      toast.success(t('ui.pinned.unpinned'));
      // Fire bus + refetch so PinnedBar AND PinnedMessagesButton stay in sync.
      emitAppEvent('chat:pins-changed');
      props.onUnpinned();
    } catch (err) {
      toast.error(...errToToast(err, t('ui.pinned.unpinFailed')));
    } finally {
      setUnpinning(false);
    }
  }
  const m = props.message;
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={props.onJump}
        className="block w-full rounded-lg px-3 py-2 pr-9 text-left text-xs hover:bg-accent/10"
      >
        <header className="mb-0.5 flex items-baseline justify-between gap-2">
          <span className="truncate text-[11px] font-light tracking-[-0.01em] text-accent">
            {m.author.displayName || m.author.username}
          </span>
          <span className="shrink-0 text-[10px] tabular-nums text-ink-muted">
            {formatTime(new Date(m.createdAt))}
          </span>
        </header>
        <p className="line-clamp-2 leading-snug text-ink-dim">
          {stripMarkdown(m.content) || t('ui.pinned.emptyContent')}
        </p>
      </button>
      <Tooltip label={t('ui.pinned.unpin')}>
        <button
          type="button"
          onClick={onUnpin}
          disabled={unpinning}
          aria-label={t('ui.pinned.unpin')}
          className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-lg text-ink-muted opacity-0 transition-opacity hover:bg-danger/15 hover:text-danger-ink group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <CloseIcon className="h-3.5 w-3.5" />
        </button>
      </Tooltip>
    </div>
  );
}

/**
 * 📌 icon + popover with the channel's pinned messages. Clicking a row
 * scrolls the message list to that message via the chat:scroll-to-
 * message bus event (handled in MessagesList).
 */
export function PinnedMessagesButton(props: { channelId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Shared pinned-messages hook — fetch + chat:pins-changed bus + real
  // loading/error states live in lib/usePinnedMessages. `enabled=open`
  // skips the network until the popover first opens.
  const { messages, loading, error, refetch } = usePinnedMessages(
    props.channelId,
    open,
  );

  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <Tooltip label={t('ui.pinned.pinnedMessages')}>
        <Popover.Trigger
          aria-label={t('ui.pinned.pinnedMessages')}
          className="group rounded-lg p-2 text-ink-dim transition-colors hover:bg-white/10 hover:text-accent"
        >
          <PinIcon className="h-[22px] w-[22px] group-hover:[animation:icon-shake_0.4s_ease-in-out_2]" />
        </Popover.Trigger>
      </Tooltip>
      <Popover.Content className="w-80 max-w-[calc(100vw-1.5rem)]" placement="bottom end">
        <Popover.Dialog className="overflow-hidden p-0">
        <div className="border-b border-bg-raised/40 px-4 py-2.5">
          <div className="text-base font-light tracking-[-0.02em] text-ink">
            {t('ui.pinned.heading')}
            {messages && messages.length > 0 && (
              <span className="ml-2 text-xs text-ink-muted">{messages.length}</span>
            )}
          </div>
        </div>
        <div className="scrollbar-thin max-h-[60vh] overflow-y-auto">
          {loading && !messages && (
            <div className="px-4 py-6 text-center text-xs text-ink-muted">
              {t('ui.pinned.loading')}
            </div>
          )}
          {error && (
            <div className="px-4 py-3 text-xs text-danger-ink">{error}</div>
          )}
          {!loading && !error && messages && messages.length === 0 && (
            <div className="px-4 py-6 text-center text-xs leading-relaxed text-ink-muted">
              {t('ui.pinned.emptyTitle')}
              <br />
              <span className="text-[11px]">
                {t('ui.pinned.emptyHint')}
              </span>
            </div>
          )}
          {!error &&
            messages &&
            messages.map((m) => (
              <PinnedPopoverRow
                key={m.id}
                message={m}
                onJump={() => {
                  emitAppEvent('chat:scroll-to-message', { messageId: m.id });
                  setOpen(false);
                }}
                onUnpinned={refetch}
              />
            ))}
        </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

/**
 * One row inside the pin-popover. Shows author + time + 3-line snippet,
 * with a hover-revealed "Открепить" close-X. Click anywhere else on the
 * row jumps to the message and closes the popover.
 */
function PinnedPopoverRow(props: {
  message: Message;
  onJump: () => void;
  onUnpinned: () => void;
}) {
  const { t } = useTranslation();
  const [unpinning, setUnpinning] = useState(false);
  async function onUnpin(e: React.MouseEvent) {
    e.stopPropagation();
    if (unpinning) return;
    setUnpinning(true);
    try {
      await api.unpinMessage(props.message.id);
      toast.success(t('ui.pinned.unpinned'));
      emitAppEvent('chat:pins-changed');
      props.onUnpinned();
    } catch (err) {
      toast.error(...errToToast(err, t('ui.pinned.unpinFailed')));
    } finally {
      setUnpinning(false);
    }
  }
  const m = props.message;
  return (
    <div
      className="group relative"
      style={{ borderBottom: '1px solid rgb(var(--ring-inset) / var(--ring-inset-alpha))' }}
    >
      <button
        type="button"
        onClick={props.onJump}
        className="block w-full px-4 py-3 pr-10 text-left transition-colors hover:bg-accent/10"
      >
        <header className="mb-1 flex items-baseline justify-between gap-2">
          <span className="truncate text-xs font-light tracking-[-0.015em] text-accent">
            {m.author.displayName || m.author.username}
          </span>
          <span className="shrink-0 text-[10px] text-ink-muted">
            {formatTime(new Date(m.createdAt))}
          </span>
        </header>
        <p className="line-clamp-3 text-xs leading-snug text-ink">
          {stripMarkdown(m.content) || t('ui.pinned.emptyContent')}
        </p>
      </button>
      <Tooltip label={t('ui.pinned.unpin')}>
        <button
          type="button"
          onClick={onUnpin}
          disabled={unpinning}
          aria-label={t('ui.pinned.unpin')}
          className="absolute right-3 top-3 grid h-6 w-6 place-items-center rounded-lg text-ink-muted opacity-0 transition-opacity hover:bg-danger/15 hover:text-danger-ink group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <CloseIcon className="h-3.5 w-3.5" />
        </button>
      </Tooltip>
    </div>
  );
}
